import { useContext, useEffect, useRef } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import {
	OnBackgroundJobProgressDocument,
	type OnBackgroundJobProgressSubscription
} from "~/gql/subscriptions/backgroundJob.generated";
import describeSubscriptionClose from "~/helpers/describeSubscriptionClose";
import logger from "~/helpers/logger";
import { useAppDispatch } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useSubscriptionClient from "~/hooks/useSubscriptionClient";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { recreateSubscriptionClientOnUnauthorized } from "~/services/subscriptionClient";

const useBackgroundJobSubscription = (): void => {
	const subscriptionClient = useSubscriptionClient();
	const dispatch = useAppDispatch();
	const { loggedIn } = useContext(AuthContext);
	const { isEnabled } = useFeatureFlag(FeatureFlagName.BackgroundJobs);
	const sawDisconnect = useRef(false);

	useEffect(() => {
		if (!loggedIn || !isEnabled) return;

		const refetchJobs = () => {
			dispatch(apiEndpoints.util.invalidateTags(["BackgroundJobs"]));
		};

		const disposeClosed = subscriptionClient.on("closed", () => {
			sawDisconnect.current = true;
		});
		const disposeConnected = subscriptionClient.on("connected", () => {
			if (!sawDisconnect.current) return;
			sawDisconnect.current = false;
			refetchJobs();
		});

		const cleanup = subscriptionClient.subscribe<OnBackgroundJobProgressSubscription>(
			{ query: OnBackgroundJobProgressDocument.toString() },
			{
				complete: () => {},
				error: (err) => {
					const closed = describeSubscriptionClose(err);
					recreateSubscriptionClientOnUnauthorized(closed.code);
					logger.error(
						`[Background job subscription] close code=${closed.code ?? "none"} clean=${closed.wasClean ?? "n/a"} reason=${closed.reason}`,
						err,
						{ ...closed }
					);
					refetchJobs();
				},
				next: () => refetchJobs()
			}
		);

		return () => {
			disposeClosed();
			disposeConnected();
			cleanup();
		};
	}, [isEnabled, loggedIn, subscriptionClient]);
};

export default useBackgroundJobSubscription;
